import { jwtDecode } from 'jwt-decode';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

export interface DecodedJwt {
  sub: string;
  email?: string;
  name?: string;
  tenant_id?: string;
  roles?: string[];
  ['_couchdb.roles']?: string[];
  impersonating?: boolean;
  exp: number;
  iat?: number;
}

const REFRESH_KEY = 'ft.refreshToken';
const SUPER_TOKEN_KEY = 'ft.superToken';

type Listener = () => void;

interface AuthState {
  jwt: string | null;
  decoded: DecodedJwt | null;
}

const state: AuthState = { jwt: null, decoded: null };
const listeners = new Set<Listener>();

function emit() {
  listeners.forEach(l => {
    try {
      l();
    } catch {
      // ignore listener errors
    }
  });
}

function safeDecode(jwt: string | null): DecodedJwt | null {
  if (!jwt) return null;
  try {
    const d = jwtDecode<DecodedJwt>(jwt);
    // The API mints CouchDB-shaped claims with `_couchdb.roles`; mirror it onto
    // `roles` so consumers don't need to know about the alternate key.
    if (!d.roles && d['_couchdb.roles']) {
      d.roles = d['_couchdb.roles'];
    }
    return d;
  } catch {
    return null;
  }
}

export function setJwt(jwt: string | null): void {
  state.jwt = jwt;
  state.decoded = safeDecode(jwt);
  emit();
}

export function getJwt(): string | null {
  return state.jwt;
}

export function getDecoded(): DecodedJwt | null {
  return state.decoded;
}

export function onChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isExpired(claims: DecodedJwt | null): boolean {
  if (!claims?.exp) return true;
  // jwt exp is seconds since epoch
  return claims.exp * 1000 <= Date.now();
}

export async function getRefreshToken(): Promise<string | null> {
  try {
    return ((await idbGet(REFRESH_KEY)) as string | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function setRefreshToken(token: string | null): Promise<void> {
  if (token == null) {
    await idbDel(REFRESH_KEY);
  } else {
    await idbSet(REFRESH_KEY, token);
  }
}

export async function clearAll(): Promise<void> {
  setJwt(null);
  await setRefreshToken(null);
  try {
    await idbDel(SUPER_TOKEN_KEY);
  } catch {
    // ignore
  }
}

// Impersonation -------------------------------------------------------------

/**
 * Switch the active access token to an impersonation JWT. The previous
 * super_admin token (or whatever was active) is stashed in IndexedDB so we can
 * restore it via endImpersonation().
 */
export async function setImpersonationToken(
  impToken: string,
  originalToken?: string
): Promise<void> {
  const stash = originalToken ?? state.jwt;
  if (stash) {
    try {
      await idbSet(SUPER_TOKEN_KEY, stash);
    } catch {
      // ignore — fall through; user will simply have to log back in
    }
  }
  setJwt(impToken);
}

/**
 * Restore the super_admin token saved by setImpersonationToken(). Returns the
 * restored token, or null if nothing was stashed.
 */
export async function endImpersonation(): Promise<string | null> {
  let original: string | null = null;
  try {
    original = ((await idbGet(SUPER_TOKEN_KEY)) as string | undefined) ?? null;
  } catch {
    original = null;
  }
  try {
    await idbDel(SUPER_TOKEN_KEY);
  } catch {
    // ignore
  }
  if (original) {
    setJwt(original);
  } else {
    setJwt(null);
  }
  return original;
}

export function isImpersonating(): boolean {
  return !!state.decoded?.impersonating;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

async function rawJson(path: string, body: unknown): Promise<Response> {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';
  return fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function login(
  email: string,
  password: string
): Promise<DecodedJwt> {
  const res = await rawJson('/auth/login', { email, password });
  if (!res.ok) {
    const err = new Error(
      res.status === 401 ? 'Invalid credentials' : 'Login failed'
    );
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const data = (await res.json()) as LoginResponse;
  await setRefreshToken(data.refreshToken);
  setJwt(data.accessToken);
  const decoded = getDecoded();
  if (!decoded) throw new Error('Invalid token returned');
  return decoded;
}

let refreshInFlight: Promise<string> | null = null;

export async function refresh(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const rt = await getRefreshToken();
    if (!rt) throw new Error('No refresh token');
    const res = await rawJson('/auth/refresh', { refreshToken: rt });
    if (!res.ok) {
      await clearAll();
      throw new Error('Refresh failed');
    }
    const data = (await res.json()) as LoginResponse;
    if (data.refreshToken) await setRefreshToken(data.refreshToken);
    setJwt(data.accessToken);
    return data.accessToken;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function logout(): Promise<void> {
  try {
    const rt = await getRefreshToken();
    if (rt) await rawJson('/auth/logout', { refreshToken: rt });
  } catch {
    // best effort
  } finally {
    // Destroy the local replica BEFORE clearing tokens. On a shared device the
    // next sign-in must not see the previous tenant's cached customer data.
    // Awaited so the `ft-local` IndexedDB is gone before any re-login can
    // resurrect it via getPouchDB(); the AuthGate teardown only fires this
    // fire-and-forget, which races a fast re-login. Dynamic import breaks the
    // static cycle (couch.ts imports auth.ts).
    try {
      const { destroyLocal } = await import('@/lib/couch');
      await destroyLocal();
    } catch {
      // best effort — token clear below still runs
    }
    await clearAll();
  }
}

export async function forgotPassword(email: string): Promise<void> {
  try {
    await rawJson('/auth/forgot', { email });
  } catch {
    // intentionally swallow — never leak existence
  }
}

export async function bootstrap(): Promise<DecodedJwt | null> {
  // attempt silent refresh from persisted refresh token
  try {
    await refresh();
    return getDecoded();
  } catch {
    return null;
  }
}
